/**
 * <SendForESignModal> — wraps Leegality send + status polling for a rent
 * agreement. Lets the owner pick e-sign mode (Aadhaar OTP / virtual / DSC),
 * add extra signers (guarantor / spouse), and confirm credit debit before
 * the API call.
 *
 * Hits these endpoints (already built backend-side):
 *   POST   /api/agreements/{id}/leegality/send
 *   GET    /api/agreements/{id}/leegality/status
 */
import React, { useState } from 'react';
import axios from 'axios';
import { Send, ShieldCheck, Plus, X, Loader2, ExternalLink } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { useToast } from '../../hooks/use-toast';
import { useAuth } from '../../context/AuthContext';
import { track } from '../../services/analytics';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MODES = [
  {
    id: 'aadhaar',
    label: 'Aadhaar OTP',
    blurb: 'Tenant signs with their Aadhaar-linked phone number. Legally valid under IT Act 2000 §3A.',
    recommended: true,
  },
  {
    id: 'virtual',
    label: 'Virtual Sign',
    blurb: 'Click-to-sign — quick but not Aadhaar-backed. Use for low-stakes letters.',
  },
  {
    id: 'dsc',
    label: 'Digital Signature Certificate',
    blurb: 'For signers with a DSC USB token. Highest legal weight.',
  },
];

const SendForESignModal = ({ open, onClose, agreement, onSent }) => {
  const { token } = useAuth();
  const { toast } = useToast();
  const [mode, setMode] = useState('aadhaar');
  const [profileId, setProfileId] = useState('');
  const [extraSigners, setExtraSigners] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  // Each extra-signer row needs a stable React key. Generating one at row
  // creation prevents wrong-row state on add/remove (input focus jumping,
  // values bleeding between rows). Falls back to a counter if crypto is
  // unavailable (very old browsers / SSR).
  const makeRowId = () =>
    (typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : `row-${Date.now()}-${Math.random()}`);

  const addSigner = () => {
    setExtraSigners(s => [...s, { _rid: makeRowId(), name: '', email: '', phone: '' }]);
  };
  const updateSigner = (i, field, v) => {
    setExtraSigners(s => s.map((row, idx) => (idx === i ? { ...row, [field]: v } : row)));
  };
  const removeSigner = (i) => {
    setExtraSigners(s => s.filter((_, idx) => idx !== i));
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const body = {
        mode,
        profile_id: profileId.trim() || null,
        extra_signers: extraSigners.filter(s => s.name && s.phone),
      };
      const res = await axios.post(
        `${API}/agreements/${agreement.id}/leegality/send`,
        body,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      track('agreement_sent_for_esign', {
        agreement_id: agreement.id,
        mode,
        extra_signers: body.extra_signers.length,
        credits_debited: res.data?.credits_debited,
      });
      toast({
        title: 'Sent for e-signing',
        description: `Leegality has notified all signers via SMS + email. ${res.data?.credits_debited || 0} credits used.`,
      });
      onSent?.(res.data);
      onClose();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map(d => d.msg || String(d)).join(', ')
        : detail || err.message;
      toast({
        title: err.response?.status === 402 ? 'Not enough credits' : `Could not send (${err.response?.status || '?'})`,
        description: msg,
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto" data-testid="esign-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck size={18} className="text-teal-600" />
            Send for e-signing
          </DialogTitle>
          <p className="text-sm text-gray-500">
            Powered by Leegality. Legally valid under the IT Act 2000.
          </p>
        </DialogHeader>

        <div className="space-y-4">
          {/* Agreement summary */}
          <div className="bg-gray-50 rounded-lg p-3 ring-1 ring-gray-200 text-sm">
            <p className="font-semibold text-gray-900">{agreement?.lessee_name}</p>
            <p className="text-gray-600">
              Room {agreement?.room_number} · ₹{agreement?.monthly_rent?.toLocaleString('en-IN')}/month · {agreement?.lease_start_date} → {agreement?.lease_end_date}
            </p>
          </div>

          {/* Mode picker */}
          <div>
            <Label>Signing method *</Label>
            <div className="space-y-2 mt-1">
              {MODES.map(m => (
                <label
                  key={m.id}
                  className={`flex items-start gap-3 p-3 rounded-lg ring-1 cursor-pointer transition-colors ${
                    mode === m.id ? 'ring-teal-500 bg-teal-50' : 'ring-gray-200 hover:bg-gray-50'
                  }`}
                  data-testid={`esign-mode-${m.id}`}
                >
                  <input
                    type="radio"
                    name="esign-mode"
                    value={m.id}
                    checked={mode === m.id}
                    onChange={() => setMode(m.id)}
                    className="mt-1"
                  />
                  <div className="flex-1">
                    <p className="font-semibold text-sm text-gray-900 flex items-center gap-2">
                      {m.label}
                      {m.recommended && (
                        <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-green-100 text-green-700">
                          Recommended
                        </span>
                      )}
                    </p>
                    <p className="text-xs text-gray-600 mt-0.5">{m.blurb}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Optional Leegality profile ID — owners with custom stamp paper templates */}
          <div>
            <Label>Leegality profile ID (optional)</Label>
            <Input
              value={profileId}
              onChange={(e) => setProfileId(e.target.value)}
              placeholder="Leave blank to use your default e-stamp profile"
              data-testid="esign-profile-id"
            />
            <p className="text-xs text-gray-500 mt-1">
              Override only if you maintain multiple stamp paper templates in Leegality.
            </p>
          </div>

          {/* Extra signers */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label>Extra signers (optional)</Label>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={addSigner}
                data-testid="esign-add-signer"
              >
                <Plus size={12} className="mr-1" /> Add
              </Button>
            </div>
            {extraSigners.length === 0 && (
              <p className="text-xs text-gray-500">
                You + the tenant are added automatically. Add a guarantor, spouse, or co-signer if needed.
              </p>
            )}
            {extraSigners.map((s, i) => (
              <div key={s._rid} className="grid grid-cols-7 gap-2 mb-2 items-center" data-testid={`esign-signer-row-${i}`}>
                <Input
                  className="col-span-2"
                  placeholder="Name"
                  value={s.name}
                  onChange={(e) => updateSigner(i, 'name', e.target.value)}
                />
                <Input
                  className="col-span-2"
                  placeholder="Email"
                  value={s.email}
                  onChange={(e) => updateSigner(i, 'email', e.target.value)}
                />
                <Input
                  className="col-span-2"
                  placeholder="Phone"
                  value={s.phone}
                  onChange={(e) => updateSigner(i, 'phone', e.target.value)}
                />
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() => removeSigner(i)}
                  className="text-red-600 hover:bg-red-50"
                >
                  <X size={14} />
                </Button>
              </div>
            ))}
          </div>

          {/* Credit cost note */}
          <div className="bg-amber-50 ring-1 ring-amber-200 rounded-lg p-3 text-xs text-amber-900">
            Sending will debit credits from your wallet based on the chosen mode and signer count.
            If Leegality rejects the request, credits are automatically refunded.
          </div>

          {/* Actions */}
          <div className="flex gap-2 pt-2">
            <Button
              variant="outline"
              onClick={onClose}
              className="flex-1"
              disabled={submitting}
              data-testid="esign-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={submitting}
              className="flex-1 bg-teal-600 hover:bg-teal-700"
              data-testid="esign-submit"
            >
              {submitting ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Send size={14} className="mr-1" />}
              {submitting ? 'Sending…' : 'Send for signing'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Small helper component — call to refresh status of a single agreement.
 * Shows signed PDF / audit URL once available.
 */
export const ESignStatusActions = ({ agreement, onUpdated }) => {
  const { token } = useAuth();
  const { toast } = useToast();
  const [polling, setPolling] = useState(false);

  const refreshStatus = async () => {
    setPolling(true);
    try {
      const res = await axios.get(
        `${API}/agreements/${agreement.id}/leegality/status`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast({
        title: 'Status refreshed',
        description: `Current state: ${res.data?.leegality_status || 'unknown'}`,
      });
      onUpdated?.(res.data);
    } catch (err) {
      toast({
        title: 'Refresh failed',
        description: err.response?.data?.detail || err.message,
        variant: 'destructive',
      });
    } finally {
      setPolling(false);
    }
  };

  return (
    <div className="flex gap-2 flex-wrap">
      <Button
        size="sm"
        variant="outline"
        onClick={refreshStatus}
        disabled={polling}
        data-testid={`esign-refresh-${agreement.id}`}
      >
        {polling ? <Loader2 size={12} className="mr-1 animate-spin" /> : <ShieldCheck size={12} className="mr-1" />}
        Refresh status
      </Button>
      {agreement.leegality_signed_url && (
        <a
          href={agreement.leegality_signed_url}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`esign-signed-link-${agreement.id}`}
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-green-600 hover:bg-green-700 text-white text-xs font-semibold transition-colors"
        >
          <ExternalLink size={12} /> Signed PDF
        </a>
      )}
      {agreement.leegality_audit_url && (
        <a
          href={agreement.leegality_audit_url}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`esign-audit-link-${agreement.id}`}
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-white ring-1 ring-gray-300 hover:bg-gray-50 text-gray-700 text-xs font-semibold transition-colors"
        >
          <ExternalLink size={12} /> Audit trail
        </a>
      )}
    </div>
  );
};

export default SendForESignModal;
